import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Helper function to draw an image on canvas with "cover" fit
const drawImageProp = (ctx, img, x, y, w, h, offsetX = 0.5, offsetY = 0.5) => {
    const iw = img.width;
    const ih = img.height;
    const r = Math.min(w / iw, h / ih);
    let nw = iw * r;
    let nh = ih * r;
    let cx, cy, cw, ch, ar = 1;

    if (nw < w) ar = w / nw;
    if (Math.abs(ar - 1) < 1e-14 && nh < h) ar = h / nh;
    nw *= ar;
    nh *= ar;

    cw = iw / (nw / w);
    ch = ih / (nh / h);

    cx = (iw - cw) * offsetX;
    cy = (ih - ch) * offsetY;

    if (cx < 0) cx = 0;
    if (cy < 0) cy = 0;
    if (cw > iw) cw = iw;
    if (ch > ih) ch = ih;

    ctx.drawImage(img, cx, cy, cw, ch, x, y, w, h);
};

const AncientSofiaHook = ({ onGameStart }) => {
    const { i18n } = useTranslation();
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isRevealed, setIsRevealed] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [revealPercent, setRevealPercent] = useState(0);
    const drawCountRef = useRef(0);

    const texts = {
        bg: {
            canvasTitle: 'Избърши прахта на времето...',
            canvasSubtitle: 'Търкай екрана, за да разкриеш тайната',
            title: 'Добре дошъл в Сердика!',
            desc: 'Под краката ти лежат над 7000 години история. Готов ли си да откриеш тайните, които този град крие от векове?',
            btn: 'Разкрий първата загадка'
        },
        en: {
            canvasTitle: 'Wipe away the dust of time...',
            canvasSubtitle: 'Rub the screen to reveal the secret',
            title: 'Welcome to Serdica!',
            desc: 'Over 7000 years of history lie beneath your feet. Are you ready to discover the secrets this city has hidden for centuries?',
            btn: 'Reveal the first riddle'
        }
    };

    const currentLang = i18n.language === 'bg' ? 'bg' : 'en';
    const t = texts[currentLang];

    // Load custom premium fonts dynamically
    useEffect(() => {
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@600;700&family=Lora:ital,wght@0,400;0,600;1,400&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        return () => {
            document.head.removeChild(link);
        };
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const container = containerRef.current;

        // Size canvas to its container
        canvas.width = container.offsetWidth;
        canvas.height = container.offsetHeight;

        const img = new Image();
        img.src = '/assets/София.jpg';
        img.onload = () => {
            // Draw modern Sofia image with "cover" fit
            drawImageProp(ctx, img, 0, 0, canvas.width, canvas.height);

            // Add dark "dust of time" overlay
            ctx.fillStyle = 'rgba(15, 10, 5, 0.45)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Add golden sparkles / dust particles to canvas texture
            ctx.fillStyle = 'rgba(212, 175, 55, 0.25)';
            for (let i = 0; i < 300; i++) {
                const x = Math.random() * canvas.width;
                const y = Math.random() * canvas.height;
                const size = Math.random() * 2 + 1;
                ctx.beginPath();
                ctx.arc(x, y, size, 0, Math.PI * 2);
                ctx.fill();
            }

            // Draw helper text with styling and shadow
            ctx.font = 'bold 22px "Cinzel", "Georgia", serif';
            ctx.fillStyle = '#ffffff';
            ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
            ctx.shadowBlur = 6;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 2;
            ctx.textAlign = 'center';
            ctx.fillText(t.canvasTitle, canvas.width / 2, canvas.height / 2 - 20);

            ctx.font = 'italic 15px "Cinzel", "Georgia", serif';
            ctx.fillStyle = '#f3e5f5';
            ctx.fillText(t.canvasSubtitle, canvas.width / 2, canvas.height / 2 + 20);

            // Reset shadow for drawing operations
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;

            // Set up brush for scratching
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';
            ctx.lineWidth = 55; // brush size
            ctx.globalCompositeOperation = 'destination-out';
        };
    }, [i18n.language]);

    // Calculate reveal progress
    const checkProgress = () => {
        if (isRevealed) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;

        let transparent = 0;
        const step = 40;
        for (let i = 3; i < pixels.length; i += step) {
            if (pixels[i] === 0) transparent++;
        }

        const total = pixels.length / step;
        const percent = (transparent / total) * 100;

        // Scale the percent so it reaches 100% when actual percent is 70%
        const scaledPercent = Math.min(100, (percent / 70) * 100);
        setRevealPercent(scaledPercent);

        // Auto-reveal if 70% cleared
        if (percent > 70) {
            setIsRevealed(true);
        }
    };

    // --- Drawing Controls ---
    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    const startDrawing = (e) => {
        if (isRevealed) return;
        setIsDrawing(true);
        const { x, y } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e) => {
        if (!isDrawing || isRevealed) return;
        e.preventDefault(); // Stop mobile scroll when scratching
        const { x, y } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.lineTo(x, y);
        ctx.stroke();

        drawCountRef.current++;
        if (drawCountRef.current % 5 === 0) {
            checkProgress();
        }
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        checkProgress();
    };

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                height: '100vh',
                maxWidth: '500px',
                margin: '0 auto',
                overflow: 'hidden',
                backgroundColor: '#0f0a05',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                backgroundImage: 'url("/assets/Сердика.png")',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            {/* Reveal Percentage Badge */}
            {!isRevealed && (
                <div style={{
                    position: 'absolute',
                    top: '30px',
                    zIndex: 3,
                    background: 'rgba(15, 10, 5, 0.85)',
                    border: '1px solid rgba(212, 175, 55, 0.5)',
                    padding: '8px 20px',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    fontFamily: '"Cinzel", "Georgia", serif',
                    color: '#d4af37',
                    pointerEvents: 'none',
                    backdropFilter: 'blur(5px)',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.6)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    transition: 'opacity 0.5s ease'
                }}>
                    {i18n.language === 'bg' ? 'Разкрити' : 'Revealed'}: {Math.round(revealPercent)}%
                </div>
            )}

            {/* Revealed content overlay */}
            <div
                style={{
                    position: 'absolute',
                    zIndex: 1,
                    textAlign: 'center',
                    padding: '35px 25px',
                    width: '85%',
                    maxWidth: '400px',
                    background: 'rgba(15, 10, 5, 0.85)',
                    border: '2px solid #d4af37',
                    borderRadius: '20px',
                    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.8), 0 0 25px rgba(212, 175, 55, 0.25)',
                    backdropFilter: 'blur(8px)',
                    opacity: isRevealed ? 1 : 0,
                    transform: isRevealed ? 'scale(1)' : 'scale(0.85)',
                    transition: 'all 1s cubic-bezier(0.19, 1, 0.22, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <h1 style={{
                    fontSize: '26px',
                    fontFamily: '"Cinzel", "Georgia", serif',
                    marginBottom: '15px',
                    color: '#d4af37',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.6), 0 0 10px rgba(212, 175, 55, 0.3)',
                    letterSpacing: '1px'
                }}>
                    {t.title}
                </h1>
                <p style={{
                    fontSize: '15px',
                    fontFamily: '"Lora", "Georgia", serif',
                    color: '#e5dcd3',
                    marginBottom: '30px',
                    lineHeight: '1.6',
                    textAlign: 'center'
                }}>
                    {t.desc}
                </p>
                <button
                    onClick={onGameStart}
                    style={{
                        padding: '14px 28px',
                        fontSize: '15px',
                        fontFamily: '"Cinzel", "Georgia", serif',
                        fontWeight: 'bold',
                        color: '#1a1005',
                        backgroundColor: '#d4af37',
                        border: 'none',
                        borderRadius: '30px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4), inset 0 0 10px rgba(255,255,255,0.4)',
                        transition: 'all 0.3s ease',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 175, 55, 0.6), inset 0 0 10px rgba(255,255,255,0.4)';
                        e.currentTarget.style.backgroundColor = '#f3cd57';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.4), inset 0 0 10px rgba(255,255,255,0.4)';
                        e.currentTarget.style.backgroundColor = '#d4af37';
                    }}
                >
                    {t.btn}
                </button>
            </div>

            {/* Scratch layer canvas */}
            <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 2,
                    opacity: isRevealed ? 0 : 1,
                    transition: 'opacity 0.8s ease-out',
                    pointerEvents: isRevealed ? 'none' : 'auto'
                }}
            />
        </div>
    );
};

export default AncientSofiaHook;